import { Form } from "common/Forms";
import { PrimaryButton, SecondaryButton } from "common/Forms/button";
import { ActionBar, CommonPage, Content, InRow, LoadableContent, OpenablePanel, PageTitle, ResponsiveGap } from "common/Grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableView } from "./TableView";
import { WithPagination } from "common/Pagination";
import { AdvanceSearchForm } from "./AdvanceSearch";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { convertDateToJson, convertJsonToDate } from "Constant/utils";
import { GrClose } from "react-icons/gr";
import { useGetOrdersMutation } from "store/action";
import { useGetSiteIdOptionsMutation } from "store/action/options-store";

export const Page = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [getOrdersApi, {isLoading, isError}] = useGetOrdersMutation();
  const pagedOrderInfo = useSelector((state) => state?.orders);
  const [searchParams, setSearchParams] = useSearchParams();
  const [getSiteIdOptionsApi] = useGetSiteIdOptionsMutation();
  const [siteIdOptions, setSiteIdOptions] = useState([]);

  const searchFields = useMemo(() =>
  ({
    startDateFrom: { name: 'startDateFrom', default: undefined, },
    endDateFrom: { name: 'endDateFrom', default: undefined, },
    siteid: { name: 'siteid', default: '10000', },
    status: { name: 'status', default: undefined, },
    orderId: { name: 'orderId', default: undefined, },
    page: { name: 'p', default: 1, },
    limit: { name: 'ps', default: 10, },
  }), []);

  const getSearchValue = useCallback((name, defaultValue, getter = (value) => value) => {
    const value = searchParams.get(name);
    if (value === null) {
      return defaultValue;
    }
    return getter(value);
  }, [searchParams])

  const page = useRef(getSearchValue(searchFields.page.name, searchFields.page.default, x => parseInt(x) ?? searchFields.page.default));
  const limit = useRef(getSearchValue(searchFields.limit.name, searchFields.limit.default, x => parseInt(x) ?? searchFields.limit.default));

  const [advancedSearchFormUserInput, setAdvancedSearchFormUserInput] = useState({
    startDateFrom: getSearchValue(searchFields.startDateFrom.name, searchFields.startDateFrom.default, value => convertJsonToDate(value)),
    endDateFrom: getSearchValue(searchFields.endDateFrom.name, searchFields.endDateFrom.default, value => convertJsonToDate(value)),
    siteid: getSearchValue(searchFields.siteid.name, searchFields.siteid.default),
    status: getSearchValue(searchFields.status.name, searchFields.status.default),
    orderId: getSearchValue(searchFields.orderId.name, searchFields.orderId.default),
  })

  const isAdvancedSearchFilters = advancedSearchFormUserInput?.startDateFrom !== undefined
    || advancedSearchFormUserInput?.endDateFrom !== undefined
    || advancedSearchFormUserInput.siteid !== undefined
    || advancedSearchFormUserInput.status !== undefined
    || advancedSearchFormUserInput.orderId !== undefined;

  const [isAdvancedSearch, setIAdvancedSearch] = useState(isAdvancedSearchFilters);

  const searchAsync = useCallback(async () => {
    await getOrdersApi(
      {
        startDate: getSearchValue(searchFields.startDateFrom.name, searchFields?.startDateFrom.default),
        endDate: getSearchValue(searchFields.endDateFrom.name, searchFields?.endDateFrom.default),
        siteid: getSearchValue(searchFields.siteid.name, searchFields.siteid.default),
        status: getSearchValue(searchFields.status.name, searchFields.status.default),
        orderId: getSearchValue(searchFields.orderId.name, searchFields.orderId.default),
        page: page.current,
        limit: limit.current,
      }
    );

  }, [getOrdersApi, getSearchValue, searchFields]);

  const applySearch = useCallback((overrideInput) => {
    const activeInput = overrideInput ?? advancedSearchFormUserInput;
    const updatedSearchParams = createSearchParams(searchParams);
    activeInput.startDateFrom ? updatedSearchParams.set(searchFields.startDateFrom.name, convertDateToJson(activeInput.startDateFrom, true) ?? '') : updatedSearchParams.delete(searchFields.startDateFrom.name);
    activeInput.endDateFrom ? updatedSearchParams.set(searchFields.endDateFrom.name, convertDateToJson(activeInput.endDateFrom, true) ?? '') : updatedSearchParams.delete(searchFields.endDateFrom.name);
    activeInput.siteid ? updatedSearchParams.set(searchFields.siteid.name, activeInput.siteid) : updatedSearchParams.delete(searchFields.siteid.name);
    activeInput.status ? updatedSearchParams.set(searchFields.status.name, activeInput.status) : updatedSearchParams.delete(searchFields.status.name);
    activeInput.orderId ? updatedSearchParams.set(searchFields.orderId.name, activeInput.orderId) : updatedSearchParams.delete(searchFields.orderId.name);
    updatedSearchParams.set(searchFields.page.name, `${page.current}`);
    updatedSearchParams.set(searchFields.limit.name, `${limit.current}`);
    setSearchParams(updatedSearchParams);
  }, [searchFields, searchParams, advancedSearchFormUserInput, setSearchParams]);

  const onCancelSearchHandler = () => {
    setIAdvancedSearch(false);
  };

  const onPageHandler = (pageRequest) => {
    page.current = pageRequest + 1;
    applySearch();
  };

  const onPageSizeHandler = (sizeRequest) => {
    limit.current = sizeRequest;
    page.current = 1;
    applySearch();
  };

  const onApplyFilters = () => {
    page.current = 1;
    applySearch();
  };

  const onClearFilters = () => {
    const cleared = {
      startDateFrom: undefined,
      endDateFrom: undefined,
      siteid: searchFields.siteid.default,
      status: undefined,
      orderId: undefined,
    };
    setAdvancedSearchFormUserInput(cleared);
    page.current = 1;
    applySearch(cleared);
  };

  useEffect(() => {
    searchAsync();
  }, [searchAsync]);

  useEffect(() => {
    applySearch();
  }, []);

  useEffect(() => {
    async function fetchSiteIdOptions() {
      const response = await getSiteIdOptionsApi();
      const options = response?.data?.data ?? [];
      setSiteIdOptions(options);
    }
    fetchSiteIdOptions();
  }, [getSiteIdOptionsApi]);
  return (
    <CommonPage>
      <Content>
        <PageTitle title="Orders" />
        <ResponsiveGap left={false} right={false}>
          <OpenablePanel isOpenedByDefault={showSearch}
            openControlRenderer=
            {
              action =>
                <ActionBar classNames={['justify-end']}>
                  <Form>
                    <PrimaryButton action={action}>Advanced Search</PrimaryButton>
                  </Form>
                </ActionBar>
            }
            closeControlRenderer=
            {
              action =>
                <ResponsiveGap top={false} left={false} right={false}>
                  <ActionBar classNames={['justify-end']}>
                    <Form>
                      <InRow>
                        <SecondaryButton action={action}><GrClose size={20} /></SecondaryButton>
                      </InRow>
                    </Form>
                  </ActionBar>
                </ResponsiveGap>
            }
            onClose={onCancelSearchHandler}
            onOpen={() => setShowSearch(true)}>
            <AdvanceSearchForm
              siteIdOptions={siteIdOptions}
              userInput={advancedSearchFormUserInput}
              setUserInput={setAdvancedSearchFormUserInput}
              onApply={onApplyFilters}
              onClear={onClearFilters} />
          </OpenablePanel>
        </ResponsiveGap>
        <LoadableContent isLoading={isLoading}
                         isError={isError}>
          <WithPagination id='pagedData'
            currentPage={(pagedOrderInfo?.data?.data?.currentPage ?? 1) - 1}
            totalPages={pagedOrderInfo?.data?.data?.totalPages ?? 0}
            totalItems={pagedOrderInfo?.data?.data?.total ?? 0}
            pageSize={limit.current ?? 0}
            onPageChange={onPageHandler}
            onPageSizeChange={onPageSizeHandler}
            paginationWrapper={x => <ResponsiveGap>{x}</ResponsiveGap>}>
            <TableView list={pagedOrderInfo?.data?.data?.data} />
          </WithPagination>
        </LoadableContent>
      </Content>
    </CommonPage>
  )
}
